import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import apiClient from '@/lib/api';
import { Code, Plus, ExternalLink, Copy, Eye, EyeOff, RefreshCw, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const AVAILABLE_SCOPES = [
  { id: 'profile', label: 'Profile', description: 'Access basic profile info' },
  { id: 'email', label: 'Email', description: 'Access user email address' },
  { id: 'openid', label: 'OpenID', description: 'OpenID Connect identifier' },
  { id: 'offline_access', label: 'Offline Access', description: 'Refresh tokens' }
];

const DeveloperPortalPage = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleSecrets, setVisibleSecrets] = useState({});
  const [createDialog, setCreateDialog] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    website_url: '',
    privacy_policy_url: '',
    terms_url: '',
    redirect_uris: [''],
    scopes: ['profile', 'email']
  });

  useEffect(() => { fetchApps(); }, []);

  const fetchApps = async () => {
    try {
      const response = await apiClient.get('/developer/apps');
      setApps(response.data);
    } catch (error) {
      toast.error('Failed to load developer apps');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const toggleSecretVisibility = (appId) => {
    setVisibleSecrets(prev => ({ ...prev, [appId]: !prev[appId] }));
  };

  const handleCreateApp = async () => {
    if (!formData.name.trim()) { toast.error('App name is required'); return; }
    const validUris = formData.redirect_uris.filter(u => u.trim());
    if (validUris.length === 0) { toast.error('At least one redirect URI is required'); return; }

    setIsCreating(true);
    try {
      await apiClient.post('/developer/apps', { ...formData, redirect_uris: validUris });
      toast.success('OAuth app created successfully');
      setCreateDialog(false);
      setFormData({
        name: '', description: '', website_url: '', privacy_policy_url: '', terms_url: '',
        redirect_uris: [''], scopes: ['profile', 'email']
      });
      fetchApps();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create app');
    } finally {
      setIsCreating(false);
    }
  };

  const rotateSecret = async (appId) => {
    if (!confirm('Rotate the client secret? Existing integrations will stop working until updated.')) return;
    try {
      const res = await apiClient.post(`/developer/apps/${appId}/rotate-secret`);
      toast.success('Client secret rotated');
      copyToClipboard(res.data.client_secret, 'New secret');
      fetchApps();
    } catch (error) {
      toast.error('Failed to rotate secret');
    }
  };

  const deleteApp = async (appId, appName) => {
    if (!confirm(`Delete "${appName}"? This cannot be undone.`)) return;
    try {
      await apiClient.delete(`/developer/apps/${appId}`);
      toast.success('App deleted');
      fetchApps();
    } catch (error) {
      toast.error('Failed to delete app');
    }
  };

  const addRedirectUri = () => setFormData({ ...formData, redirect_uris: [...formData.redirect_uris, ''] });
  const updateRedirectUri = (index, value) => {
    const updated = [...formData.redirect_uris];
    updated[index] = value;
    setFormData({ ...formData, redirect_uris: updated });
  };
  const removeRedirectUri = (index) => {
    if (formData.redirect_uris.length === 1) return;
    setFormData({ ...formData, redirect_uris: formData.redirect_uris.filter((_, i) => i !== index) });
  };
  const toggleScope = (scopeId) => {
    const scopes = formData.scopes.includes(scopeId)
      ? formData.scopes.filter(s => s !== scopeId)
      : [...formData.scopes, scopeId];
    setFormData({ ...formData, scopes });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-pista border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading developer apps...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="developer-portal-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Developer Portal</h2>
          <p className="text-muted-foreground mt-1">Create and manage OAuth 2.1 applications</p>
        </div>
        <Button onClick={() => setCreateDialog(true)} data-testid="create-app-button">
          <Plus className="mr-2 h-4 w-4" /> Create Application
        </Button>
      </div>

      {apps.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Code className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-semibold mb-2">No applications yet</h3>
            <p className="text-muted-foreground mb-4">Create your first OAuth application to get started</p>
            <Button onClick={() => setCreateDialog(true)} data-testid="create-first-app-button">
              <Plus className="mr-2 h-4 w-4" /> Create Application
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {apps.map((app, index) => (
            <Card key={app.app_id} data-testid={`developer-app-${index}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="flex items-center space-x-2">
                      <span>{app.name}</span>
                      {app.website_url && (
                        <a href={app.website_url} target="_blank" rel="noopener noreferrer" className="text-pista hover:opacity-80">
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      )}
                    </CardTitle>
                    {app.description && <CardDescription className="mt-1">{app.description}</CardDescription>}
                  </div>
                  <div className="flex space-x-2">
                    <Button variant="outline" size="sm" onClick={() => rotateSecret(app.app_id)} data-testid={`rotate-secret-${index}`} title="Rotate secret">
                      <RefreshCw className="h-3 w-3" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => deleteApp(app.app_id, app.name)} data-testid={`delete-app-${index}`} title="Delete app">
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm font-medium">Client ID</label>
                      <Button variant="ghost" size="sm" onClick={() => copyToClipboard(app.client_id, 'Client ID')} data-testid={`copy-client-id-${index}`}>
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                    <code className="block text-xs bg-secondary p-2 rounded font-mono break-all">{app.client_id}</code>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm font-medium">Client Secret</label>
                      <div className="flex space-x-1">
                        <Button variant="ghost" size="sm" onClick={() => toggleSecretVisibility(app.app_id)} data-testid={`toggle-secret-${index}`}>
                          {visibleSecrets[app.app_id] ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => copyToClipboard(app.client_secret, 'Client Secret')} data-testid={`copy-client-secret-${index}`}>
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    <code className="block text-xs bg-secondary p-2 rounded font-mono break-all">
                      {visibleSecrets[app.app_id] ? app.client_secret : '•'.repeat(48)}
                    </code>
                  </div>

                  <div>
                    <label className="text-sm font-medium block mb-1">Redirect URIs</label>
                    <div className="space-y-1">
                      {app.redirect_uris.map((uri, uriIndex) => (
                        <code key={uriIndex} className="block text-xs bg-secondary p-2 rounded font-mono break-all">{uri}</code>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium block mb-1">Scopes</label>
                    <div className="flex flex-wrap gap-1">
                      {app.scopes.map((scope) => (
                        <Badge key={scope} variant="secondary" className="text-xs">{scope}</Badge>
                      ))}
                    </div>
                  </div>

                  <div className="pt-2 border-t border-border text-xs text-muted-foreground">
                    Created {format(new Date(app.created_at), 'MMM d, yyyy')}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-app-dialog">
          <DialogHeader>
            <DialogTitle>Create OAuth Application</DialogTitle>
            <DialogDescription>Register a new OAuth 2.1 application. You'll receive a client_id and client_secret.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="app-name">Application Name *</Label>
              <Input id="app-name" data-testid="app-name-input" placeholder="My Awesome App"
                value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
            </div>

            <div>
              <Label htmlFor="app-description">Description</Label>
              <Textarea id="app-description" data-testid="app-description-input" placeholder="What does your app do?" rows={2}
                value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
            </div>

            <div>
              <Label htmlFor="website-url">Website URL</Label>
              <Input id="website-url" data-testid="website-url-input" type="url" placeholder="https://myapp.com"
                value={formData.website_url} onChange={(e) => setFormData({ ...formData, website_url: e.target.value })} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="privacy-url">Privacy Policy URL</Label>
                <Input id="privacy-url" data-testid="privacy-url-input" type="url" placeholder="https://myapp.com/privacy"
                  value={formData.privacy_policy_url} onChange={(e) => setFormData({ ...formData, privacy_policy_url: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="terms-url">Terms URL</Label>
                <Input id="terms-url" data-testid="terms-url-input" type="url" placeholder="https://myapp.com/terms"
                  value={formData.terms_url} onChange={(e) => setFormData({ ...formData, terms_url: e.target.value })} />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Redirect URIs *</Label>
                <Button type="button" variant="ghost" size="sm" onClick={addRedirectUri} data-testid="add-redirect-uri">
                  <Plus className="h-3 w-3 mr-1" /> Add URI
                </Button>
              </div>
              <div className="space-y-2">
                {formData.redirect_uris.map((uri, index) => (
                  <div key={index} className="flex space-x-2">
                    <Input data-testid={`redirect-uri-${index}`} type="url" placeholder="https://myapp.com/callback"
                      value={uri} onChange={(e) => updateRedirectUri(index, e.target.value)} />
                    {formData.redirect_uris.length > 1 && (
                      <Button type="button" variant="outline" size="icon" onClick={() => removeRedirectUri(index)}>
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label className="block mb-2">Scopes</Label>
              <div className="space-y-2">
                {AVAILABLE_SCOPES.map((scope) => (
                  <label key={scope.id} className="flex items-start space-x-3 p-3 border border-border rounded-md cursor-pointer hover:bg-secondary/40 transition-colors">
                    <Checkbox checked={formData.scopes.includes(scope.id)} onCheckedChange={() => toggleScope(scope.id)} data-testid={`scope-${scope.id}`} />
                    <div className="flex-1">
                      <div className="font-medium text-sm">{scope.label}</div>
                      <div className="text-xs text-muted-foreground">{scope.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateApp} disabled={isCreating} data-testid="submit-create-app">
              {isCreating ? 'Creating...' : 'Create Application'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeveloperPortalPage;
